import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { 
  AlertTriangle, RefreshCw, Settings, Clock, FileText, Mail, MailX
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Type definitions
interface MaintenanceMode {
  enabled: boolean;
  message: string;
  startTime: string;
  endTime: string;
  scheduledMaintenance: MaintenanceEvent[];
}

interface MaintenanceEvent {
  id: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
}

interface SystemSettings {
  defaultLanguage: string;
  dateFormat: string;
  timeFormat: string;
  timeZone: string;
  maxFileUploadSize: number;
  maxExportResults: number;
  sessionTimeout: number;
  dataRetentionDays: number;
  showLandingFaq?: boolean;
}

interface SystemLogEntry {
  id: string;
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  source: string;
  details?: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  renewalReminderEmails: boolean;
  monthlySpendReportEmails: boolean;
  systemAlerts: boolean;
  maintenanceNotices: boolean;
  billingAlerts: boolean;
  securityAlerts: boolean;
  stopAllEmails: boolean;
}

// Default values
const defaultMaintenanceMode: MaintenanceMode = {
  enabled: false,
  message: 'System is undergoing scheduled maintenance. Please check back later.',
  startTime: '',
  endTime: '',
  scheduledMaintenance: []
};

const defaultSystemSettings: SystemSettings = {
  defaultLanguage: 'en',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  timeZone: 'UTC',
  maxFileUploadSize: 10,
  maxExportResults: 1000,
  sessionTimeout: 30,
  dataRetentionDays: 90,
  showLandingFaq: true
};

const defaultNotificationSettings: NotificationSettings = {
  emailNotifications: true,
  renewalReminderEmails: true,
  monthlySpendReportEmails: true,
  systemAlerts: true,
  maintenanceNotices: true,
  billingAlerts: true,
  securityAlerts: true,
  stopAllEmails: false,
};

const ControlsPage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('general');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const { toast } = useToast();

  // Format time functions
  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'Invalid date';
    }
  };

  // Get status indicators
  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'info': return 'bg-blue-500';
      case 'warning': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      case 'critical': return 'bg-red-700';
      default: return 'bg-gray-500';
    }
  };

  // Queries
  const { data: maintenanceMode = defaultMaintenanceMode } = useQuery({
    queryKey: ['/api/admin/system/maintenance'],
    queryFn: async () => {
      try {
        const response = await apiRequest('/api/admin/system/maintenance', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultMaintenanceMode;
      }
    },
  });

  const { data: systemSettings = defaultSystemSettings } = useQuery({
    queryKey: ['/api/admin/system/settings'],
    queryFn: async () => {
      try {
        const response = await apiRequest('/api/admin/system/settings', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultSystemSettings;
      }
    },
  });

  const { data: notificationSettings = defaultNotificationSettings } = useQuery({
    queryKey: ['/api/admin/settings/notifications'],
    queryFn: async () => {
      try {
        const response = await apiRequest('/api/admin/settings/notifications', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultNotificationSettings;
      }
    },
  });

  const { data: systemLogs = [] } = useQuery({
    queryKey: ['/api/admin/system/logs'],
    queryFn: async () => {
      try {
        const response = await apiRequest('/api/admin/system/logs', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return [];
      }
    },
  });

  // Mutations
  const updateMaintenanceMutation = useMutation({
    mutationFn: async (data: MaintenanceMode) => {
      return await apiRequest('/api/admin/system/maintenance', {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/maintenance'] });
      toast({
        title: 'Maintenance settings updated',
        description: 'Maintenance mode settings have been successfully updated.',
      });
    },
    onError: (error) => {
      console.error('Error updating maintenance mode:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update maintenance mode settings.',
        variant: 'destructive',
      });
    },
  });

  const updateSystemSettingsMutation = useMutation({
    mutationFn: async (data: SystemSettings) => {
      return await apiRequest('/api/admin/system/settings', {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/settings'] });
      toast({
        title: 'System settings updated',
        description: 'System settings have been successfully updated.',
      });
    },
    onError: (error) => {
      console.error('Error updating system settings:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update system settings.',
        variant: 'destructive',
      });
    },
  });

  const updateNotificationSettingsMutation = useMutation({
    mutationFn: async (data: NotificationSettings) => {
      return await apiRequest('/api/admin/settings/notifications', {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings/notifications'] });
      toast({
        title: 'Notification settings updated',
        description: 'Email notification controls have been successfully updated.',
      });
    },
    onError: (error) => {
      console.error('Error updating notification settings:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update notification settings.',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <SuperAdminLayout>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">System Controls</h1>
            <p className="text-slate-500">Manage system-wide settings and operations</p>
          </div>
        </div>

        <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-2 gap-1 md:grid-cols-4">
            <TabsTrigger value="general" className="flex items-center gap-1">
              <Settings className="h-4 w-4" />
              <span className="hidden md:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span className="hidden md:inline">Maintenance</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-1">
              <Mail className="h-4 w-4" />
              <span className="hidden md:inline">Notifications</span>
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              <span className="hidden md:inline">Logs</span>
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General System Settings</CardTitle>
                <CardDescription>
                  Configure general system settings and preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Default Language</label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={systemSettings.defaultLanguage}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...systemSettings, 
                          defaultLanguage: e.target.value 
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    >
                      <option value="en">English</option>
                      <option value="fr">French</option>
                      <option value="es">Spanish</option>
                      <option value="de">German</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Date Format</label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={systemSettings.dateFormat}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...systemSettings, 
                          dateFormat: e.target.value 
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Time Format</label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={systemSettings.timeFormat}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...systemSettings, 
                          timeFormat: e.target.value 
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    >
                      <option value="12h">12-hour (AM/PM)</option>
                      <option value="24h">24-hour</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Time Zone</label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={systemSettings.timeZone}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...systemSettings, 
                          timeZone: e.target.value 
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    >
                      <option value="UTC">UTC</option>
                      <option value="EST">Eastern (EST/EDT)</option>
                      <option value="CST">Central (CST/CDT)</option>
                      <option value="MST">Mountain (MST/MDT)</option>
                      <option value="PST">Pacific (PST/PDT)</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Max File Upload Size (MB)</label>
                    <Input 
                      type="number"
                      value={systemSettings.maxFileUploadSize?.toString() || '10'}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...systemSettings, 
                          maxFileUploadSize: parseInt(e.target.value) || 10 
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Max Export Results</label>
                    <Input 
                      type="number"
                      value={systemSettings.maxExportResults?.toString() || '1000'}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...systemSettings, 
                          maxExportResults: parseInt(e.target.value) || 1000
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Session Timeout (minutes)</label>
                    <Input 
                      type="number"
                      value={systemSettings.sessionTimeout?.toString() || '30'}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...systemSettings, 
                          sessionTimeout: parseInt(e.target.value) || 30
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Data Retention (days)</label>
                    <Input 
                      type="number"
                      value={systemSettings.dataRetentionDays?.toString() || '90'}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...systemSettings, 
                          dataRetentionDays: parseInt(e.target.value) || 90
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50/50 col-span-1 md:col-span-2">
                    <div className="space-y-0.5">
                      <label className="text-sm font-semibold text-slate-900">Landing Page FAQs</label>
                      <p className="text-xs text-slate-500">Enable or disable the Frequently Asked Questions section on the landing and pricing pages.</p>
                    </div>
                    <Switch
                      checked={systemSettings.showLandingFaq !== false}
                      onCheckedChange={(checked) => {
                        const updatedSettings = {
                          ...systemSettings,
                          showLandingFaq: checked
                        };
                        updateSystemSettingsMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={() => updateSystemSettingsMutation.mutate(systemSettings)}
                  disabled={updateSystemSettingsMutation.isPending}
                >
                  {updateSystemSettingsMutation.isPending ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance">
            <Card>
              <CardHeader>
                <CardTitle>Maintenance Mode</CardTitle>
                <CardDescription>
                  Configure system maintenance settings and schedule
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch 
                    checked={maintenanceMode.enabled}
                    onCheckedChange={(checked) => {
                      const updatedMaintenance = { 
                        ...maintenanceMode, 
                        enabled: checked 
                      };
                      updateMaintenanceMutation.mutate(updatedMaintenance);
                    }}
                  />
                  <label>Enable Maintenance Mode</label>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Maintenance Message</label>
                  <Textarea 
                    value={maintenanceMode.message}
                    onChange={(e) => {
                      const updatedMaintenance = { 
                        ...maintenanceMode, 
                        message: e.target.value 
                      };
                      updateMaintenanceMutation.mutate(updatedMaintenance);
                    }}
                    placeholder="System is undergoing scheduled maintenance. Please check back later."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Start Time</label>
                    <Input 
                      type="datetime-local"
                      value={maintenanceMode.startTime}
                      onChange={(e) => {
                        const updatedMaintenance = { 
                          ...maintenanceMode, 
                          startTime: e.target.value 
                        };
                        updateMaintenanceMutation.mutate(updatedMaintenance);
                      }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">End Time</label>
                    <Input 
                      type="datetime-local"
                      value={maintenanceMode.endTime}
                      onChange={(e) => {
                        const updatedMaintenance = { 
                          ...maintenanceMode, 
                          endTime: e.target.value 
                        };
                        updateMaintenanceMutation.mutate(updatedMaintenance);
                      }}
                    />
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h3 className="text-lg font-medium mb-2">Scheduled Maintenance</h3>
                  {(maintenanceMode.scheduledMaintenance || []).length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      No scheduled maintenance events
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {(maintenanceMode.scheduledMaintenance || []).map((event: MaintenanceEvent) => (
                        <div key={event.id} className="border rounded-lg p-4">
                          <div className="flex justify-between">
                            <h4 className="font-medium">{event.title}</h4>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              event.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                              event.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                              event.status === 'completed' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {event.status.replace('-', ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                          <div className="flex justify-between text-xs text-gray-500 mt-2">
                            <span>Start: {formatDate(event.startTime)}</span>
                            <span>End: {formatDate(event.endTime)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={() => updateMaintenanceMutation.mutate(maintenanceMode)}
                  disabled={updateMaintenanceMutation.isPending}
                >
                  {updateMaintenanceMutation.isPending ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Save Maintenance Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notification Settings</CardTitle>
                <CardDescription>
                  Control platform email notifications sent by background jobs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`flex items-center justify-between rounded-lg border p-4 ${notificationSettings.stopAllEmails ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center gap-3">
                    <MailX className={`h-5 w-5 ${notificationSettings.stopAllEmails ? 'text-red-600' : 'text-slate-400'}`} />
                    <div>
                      <p className="font-semibold text-sm">Stop All Emails</p>
                      <p className="text-xs text-slate-500">Block all outgoing emails except login and verification emails</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.stopAllEmails}
                    onCheckedChange={(checked) => {
                      updateNotificationSettingsMutation.mutate({
                        ...notificationSettings,
                        stopAllEmails: checked,
                      });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="font-semibold text-sm">Enable email notifications</p>
                      <p className="text-xs text-slate-500">Master setting for non-auth notification emails</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.emailNotifications}
                    disabled={notificationSettings.stopAllEmails}
                    onCheckedChange={(checked) => {
                      updateNotificationSettingsMutation.mutate({
                        ...notificationSettings,
                        emailNotifications: checked,
                      });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="font-semibold text-sm">License renewal reminder emails</p>
                      <p className="text-xs text-slate-500">Send renewal reminders before license renewal dates</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.renewalReminderEmails}
                    disabled={notificationSettings.stopAllEmails || !notificationSettings.emailNotifications}
                    onCheckedChange={(checked) => {
                      updateNotificationSettingsMutation.mutate({
                        ...notificationSettings,
                        renewalReminderEmails: checked,
                      });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="font-semibold text-sm">Monthly SaaS spend reports</p>
                      <p className="text-xs text-slate-500">Send monthly spend summaries to tenant admins</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.monthlySpendReportEmails}
                    disabled={notificationSettings.stopAllEmails || !notificationSettings.emailNotifications}
                    onCheckedChange={(checked) => {
                      updateNotificationSettingsMutation.mutate({
                        ...notificationSettings,
                        monthlySpendReportEmails: checked,
                      });
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle>System Logs</CardTitle>
                <CardDescription>
                  View system logs and audit trail
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!Array.isArray(systemLogs) || systemLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center">
                    <FileText className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium">No logs found</h3>
                    <p className="text-sm text-gray-500 mt-2">
                      System logs will appear here when available
                    </p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Array.isArray(systemLogs) && systemLogs.map((log) => (
                          <tr key={log.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                            <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatDate(log.timestamp)}</td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center gap-2">
                                <div className={`h-3 w-3 rounded-full ${getLogLevelColor(log.level)}`} />
                                <span className="capitalize">{log.level}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">{log.source}</td>
                            <td className="py-3 px-4 text-sm text-gray-500">{log.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </SuperAdminLayout>
    </div>
  );
};

export default ControlsPage;
